import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
} from '@mui/material';
import SimpleGrid from '../components/SimpleGrid';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const ProfilePage: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    age: user?.age || '',
    years_experience: user?.years_experience || '',
    current_org: user?.current_org || '',
    current_role: user?.current_role || '',
    scripted_auto_exp: user?.scripted_auto_exp || '',
    nocode_tools_exp: user?.nocode_tools_exp || '',
    country: user?.country || '',
  });

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const profileData = {
        ...formData,
        age: formData.age ? parseInt(formData.age.toString()) : undefined,
        years_experience: formData.years_experience ? parseInt(formData.years_experience.toString()) : undefined,
      };

      const isFirstTime = !user?.profile_completed;
      await updateProfile(profileData);
      setSuccess(true);

      if (isFirstTime) {
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (error: any) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Complete Your Profile
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Please provide some information about yourself to personalize your learning experience.
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
            Profile updated successfully!
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <SimpleGrid container spacing={3}>
            <SimpleGrid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name}
                onChange={handleChange('name')}
                required
              />
            </SimpleGrid>
            <SimpleGrid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Age"
                type="number"
                value={formData.age}
                onChange={handleChange('age')}
              />
            </SimpleGrid>
            <SimpleGrid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Years of Experience"
                type="number"
                value={formData.years_experience}
                onChange={handleChange('years_experience')}
              />
            </SimpleGrid>
            <SimpleGrid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Organization"
                value={formData.current_org}
                onChange={handleChange('current_org')}
              />
            </SimpleGrid>
            <SimpleGrid item xs={12}>
              <TextField
                fullWidth
                label="Current Role"
                value={formData.current_role}
                onChange={handleChange('current_role')}
              />
            </SimpleGrid>
            <SimpleGrid item xs={12}>
              <TextField
                fullWidth
                label="Previous Scripted Automation Experience"
                multiline
                rows={3}
                value={formData.scripted_auto_exp}
                onChange={handleChange('scripted_auto_exp')}
                placeholder="Describe your experience with scripted automation tools..."
              />
            </SimpleGrid>
            <SimpleGrid item xs={12}>
              <TextField
                fullWidth
                label="No-Code Automation Tools Experience"
                multiline
                rows={3}
                value={formData.nocode_tools_exp}
                onChange={handleChange('nocode_tools_exp')}
                placeholder="Describe your experience with no-code automation tools..."
              />
            </SimpleGrid>
            <SimpleGrid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={handleChange('country')}
              />
            </SimpleGrid>
          </SimpleGrid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              disabled={loading}
            >
              Skip for Now
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ProfilePage;